import { Navigate, Outlet } from "react-router-dom";
import { Suspense } from "react";

import { AuthorizationDetails } from "../modules/AllPatientRecords/routes/AuthorizationDetails";
import { Mypatients } from "../modules/MyPatients";
import { PatientInfo } from "../modules/AllPatientRecords/routes/PatientDetails";
import { ReauthReferralIntake } from "../modules/AllPatientRecords/routes/ReauthReferral";
import { ReferralIntake } from "../modules/AllPatientRecords/routes/ReferralIntake";
import {
  UserManagement,
  UserDetails,
} from "../modules/AdminCenter/routes/UserManagement";
import {
  NoteManagement,
  CreateNoteTemplate,
} from "../modules/AdminCenter/routes/NoteManagement";
import { PatientRecords } from "../modules/AllPatientRecords/routes/PatientRecords";
import { NonIndexRouteWithFullPath } from "../models/Modules";
import { Loading } from "../components/custom/Loading/components/Loading";
import { CreateRationaleTemplate, DenialRationalManagement } from "../modules/AdminCenter/routes/RationaleManagement/";
import { lazyWithRetry } from "utils";
const PatientInformationView = lazyWithRetry(() => import("../modules/AllPatientRecords/routes/PatientDetails/containers/PatientInformation"))

export const MY_PATIENT_LIST = "/my-patients";

export const ADMIN_CENTER = "/admin-center";
export const USER_MANAGEMENT = "/user-management";
export const USER_MANAGEMENT_DETAILS = "/user-management/user-details/";
export const NOTE_MANAGEMENT = "/note-management";
export const NOTE_MANAGEMENT_EDIT_TEMPLATE = "/note-management/edit-template/";
export const NOTE_MANAGEMENT_CREATE_TEMPLATE =
  "/note-management/create-template";
export const RATIONALE_MANAGEMENT = "/rationale-management";
export const RATIONALE_MANAGEMENT_CREATE_TEMPLATE =
  "/rationale-management/create-rationale-template";
export const RATIONALE_MANAGEMENT_EDIT_TEMPLATE = "/rationale-management/edit-rationale-template/";

export const ALL_RECORDS = "/allrecords";
export const AUTHORZATION_DETAIL = "/authorization-details/";

export const PATIENT_RECORD = "/patient-records";
export const PATIENT_DETAIL = "/patient-details/";
export const CREATE_PATIENT = "/create-patient";
export const REAUTH_REFERRAL_INTAKE = "/create-re-auth/";
export const REFERRAL_INTAKE = "/create-prior-auth/";

export const ReferralIntakeLink = (referralId: string, id: string, additionalParams: string) => {
  return `${PATIENT_RECORD}${REFERRAL_INTAKE}${referralId}/${id}/${additionalParams}`;
};

export const ReauthReferralIntakeLink = (
  reAuthId: string,
  referralId: string
) => {
  return `${PATIENT_RECORD}${REAUTH_REFERRAL_INTAKE}${reAuthId}/${referralId}`;
};

export const PatientDetailsForm = (id: string) => {
  return `${PATIENT_RECORD}${PATIENT_DETAIL}${id}`;
};

export const AuthorizationDetailsLink = (id: string) => {
  return `${PATIENT_RECORD}${AUTHORZATION_DETAIL}${id}`;
};

export const ReAuthCompleteAuthorizationView = (id: string, type?: string) => {
  return `${PATIENT_RECORD}${AUTHORZATION_DETAIL}${id}${type}`;
};

export const AdminCenterLink = (id: string) => {
  return `${ADMIN_CENTER}${id}`;
};

export const CreatePatient = (
  lastName: string,
  dob: string,
  healthPlan: string,
  policyId: string,
  isManual: string
) => {
  return `${PATIENT_RECORD}${CREATE_PATIENT}/${lastName}/${dob}/${healthPlan}/${policyId}/${isManual}`;
};

export const UserManagementLink = () => {
  return `${ADMIN_CENTER}${USER_MANAGEMENT}`;
};

export const UserManagementDetailsLink = (id: string) => {
  return `${ADMIN_CENTER}${USER_MANAGEMENT_DETAILS}${id}`;
};

export const NoteManagementLink = () => {
  return `${ADMIN_CENTER}${NOTE_MANAGEMENT}`;
};

export const NoteManagementCreateTemplateLink = (id: string) => {
  return `${ADMIN_CENTER}${NOTE_MANAGEMENT_EDIT_TEMPLATE}${id}`;
};

export const NoteManagementTemplateLink = () => {
  return `${ADMIN_CENTER}${NOTE_MANAGEMENT_CREATE_TEMPLATE}`;
};

export const RationaleManagementLink = () => {
  return `${ADMIN_CENTER}${RATIONALE_MANAGEMENT}`;
};

export const RationaleManagementCreateTemplateLink = () => {
  return `${ADMIN_CENTER}${RATIONALE_MANAGEMENT_CREATE_TEMPLATE}`;
};

export const RationaleManagementEditTemplateLink = (id: string) => {
  return `${ADMIN_CENTER}${RATIONALE_MANAGEMENT_EDIT_TEMPLATE}${id}`;
};

export const Layout = () => {
  return <Outlet />;
};

const routeMapping: NonIndexRouteWithFullPath[] = [
  {
    path: "/my-patients",
    element: <Suspense fallback={<Loading />}><Mypatients /></Suspense>,
    children: [],
  },
  {
    path: "/patient-records",
    element: <Suspense fallback={<Loading />}><PatientRecords /></Suspense>,
    children: [
      {
        path: "create-patient/:lastName/:dob/:healthPlan/:policyId/:isManual",
        element: <Suspense fallback={<Loading />}><PatientInfo /></Suspense>,
      },
      {
        path: "patient-details/:patientId",
        element: <Suspense fallback={<Loading />}><PatientInformationView /></Suspense>,
      },
      {
        path: "create-prior-auth/:referralId/:patientId/:additionalParams",
        element: <Suspense fallback={<Loading />}><ReferralIntake /></Suspense>,
      },
      {
        path: "authorization-details/:authNo",
        element: <Suspense fallback={<Loading />}><AuthorizationDetails /></Suspense>,
      },
      {
        path: "create-re-auth/:reAuthId/:referralId",
        element: <Suspense fallback={<Loading />}><ReauthReferralIntake /></Suspense>,
      },
    ],
  },
  {
    path: "/admin-center",
    element: <Navigate to="/admin-center/user-management" replace />,
    children: [
      {
        path: "user-management",
        element: <Suspense fallback={<Loading />}><UserManagement /></Suspense>,
        children: [
          {
            path: "user-details/:userid",
            element: <Suspense fallback={<Loading />}><UserDetails /></Suspense>,
          },
        ],
      },
      {
        path: "note-management",
        element: <Suspense fallback={<Loading />}><NoteManagement /></Suspense>,
        children: [
          {
            path: "create-template",
            element: <Suspense fallback={<Loading />}><CreateNoteTemplate /></Suspense>,
          },
          {
            path: "edit-template/:templateId",
            element: <Suspense fallback={<Loading />}><CreateNoteTemplate /></Suspense>,
          },
        ],
      },
      {
        path: "rationale-management",
        element: <Suspense fallback={<Loading />}><DenialRationalManagement/></Suspense>,
        children: [
          {
            path: "create-rationale-template",
            element: <Suspense fallback={<Loading />}><CreateRationaleTemplate /></Suspense>,
          },
          {
            path: "edit-rationale-template/:rationaleTemplateId",
            element: <Suspense fallback={<Loading />}><CreateRationaleTemplate /></Suspense>,
          },
        ],
      }
    ],
  },
];

export default routeMapping;
